import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { Eye, Pencil, Trash2, Download } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { Occurrence } from './types'
import ModalContext from '../../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../../contexts/OccurrenceContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { downloadOccurrencePdfById } from '../../../../components/occurrences/ViewOccurrenceModal'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import { OverflowCell } from '../../../../components/OverflowCell'
import {
    StandardDataTable,
    useColumnsState,
    StandardColumn,
} from '../../../../components/StandardPage'

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow: auto;
`

const occurrencesScreenKey = 'occurrences'

interface OccurrencesTableProps {
    search?: string
    appliedFilters?: Filter[]
    refresh?: boolean
    onRefreshStart?: () => void
    onRefreshEnd?: () => void
}

const OccurrencesTable: React.FC<OccurrencesTableProps> = ({
    search,
    appliedFilters = [],
    refresh,
    onRefreshStart,
    onRefreshEnd,
}) => {
    const [searchParams] = useSearchParams()
    const occurrenceIdParam = searchParams.get('occurrenceId') || searchParams.get('id')
    const openedOccurrenceRef = useRef<number | null>(null)
    const { setModalSection, setModalActive, modalActive } = useContext(ModalContext)
    const { setOccurrence } = useContext(OccurrenceContext)
    const { setFeedback } = useContext(FeedbackContext)
    const [occurrences, setOccurrences] = useState<Occurrence[]>([])
    const [loading, setLoading] = useState(true)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deletingOccurrence, setDeletingOccurrence] = useState<Occurrence | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [totalOccurrences, setTotalOccurrences] = useState<number>(0)
    const [hasNextPage, setHasNextPage] = useState<boolean>(false)
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)

    useEffect(() => {
        if (!occurrenceIdParam) return
        const parsedId = Number(occurrenceIdParam)
        if (isNaN(parsedId) || parsedId <= 0 || openedOccurrenceRef.current === parsedId) return

        openedOccurrenceRef.current = parsedId
        ApiService.occurrences.findById(parsedId)
            .then(response => {
                if (response.data) {
                    setOccurrence(response.data)
                    setModalSection('createEditOccurrenceModal')
                    setModalActive(true)
                }
            })
            .catch(error => {
                console.error(error)
            })
    }, [occurrenceIdParam])

    const ITEMS_PER_PAGE = 50

    const loadOccurrences = (page: number = 1, append: boolean = false, searchTerm?: string) => {
        if (append) {
            setIsLoadingMore(true)
        } else {
            setLoading(true)
        }

        const params: any = { page, size: ITEMS_PER_PAGE, search: searchTerm }
        if (appliedFilters && appliedFilters.length > 0) {
            params.filters = JSON.stringify(appliedFilters)
        }

        ApiService.occurrences.findAll(params)
            .then(response => {
                const returnedItems: Occurrence[] = response.data.content || []
                const total: number = response.data.totalElements || 0
                const returnedCount = returnedItems.length

                if (append) {
                    setOccurrences(prev => {
                        const existingIds = new Set(prev.map(o => o.id))
                        const newItems = returnedItems.filter(o => !existingIds.has(o.id))
                        const updated = [...prev, ...newItems]
                        const hasMore = returnedCount === ITEMS_PER_PAGE && updated.length < total
                        setHasNextPage(hasMore)
                        return updated
                    })
                } else {
                    setOccurrences(returnedItems)
                    const hasMore = returnedCount === ITEMS_PER_PAGE && returnedCount < total
                    setHasNextPage(hasMore)
                }

                setTotalOccurrences(total)
                setCurrentPage(page)
            })
            .catch(error => {
                console.error(error)
                if (!append) {
                    setOccurrences([])
                    setTotalOccurrences(0)
                    setHasNextPage(false)
                }
            })
            .finally(() => {
                if (append) {
                    setIsLoadingMore(false)
                } else {
                    setLoading(false)
                }
            })
    }

    useEffect(() => {
        loadOccurrences(1, false, search)
    }, [])

    useEffect(() => {
        setCurrentPage(1)
        loadOccurrences(1, false, search)
    }, [search, appliedFilters])

    useEffect(() => {
        if (!modalActive) {
            loadOccurrences(1, false, search)
        }
    }, [modalActive])

    const skipFirstRefresh = useRef(true)
    useEffect(() => {
        if (skipFirstRefresh.current) {
            skipFirstRefresh.current = false
            return
        }
        if (refresh === undefined) return
        if (onRefreshStart) onRefreshStart()
        loadOccurrences(1, false, search)
        if (onRefreshEnd) onRefreshEnd()
    }, [refresh])

    const handleViewOccurrence = (occurrence: Occurrence) => {
        setOccurrence(occurrence)
        setModalSection('viewOccurrenceModal')
        setModalActive(true)
    }

    const handleEditOccurrence = async (occurrence: Occurrence) => {
        try {
            const response = await ApiService.occurrences.findById(occurrence.id)
            setOccurrence(response.data)
            setModalSection('createEditOccurrenceModal')
            setModalActive(true)
        } catch (error) {
            console.error('Erro ao carregar ocorrência para edição:', error)
            setOccurrence(occurrence)
            setModalSection('createEditOccurrenceModal')
            setModalActive(true)
        }
    }

    const handleDeleteOccurrence = (occurrence: Occurrence) => {
        setDeletingOccurrence(occurrence)
        setShowDeleteModal(true)
    }

    const handleConfirmDelete = async () => {
        if (!deletingOccurrence) return
        try {
            setIsDeleting(true)
            await ApiService.occurrences.delete(deletingOccurrence.id)
            setFeedback({ message: `Ocorrência ${deletingOccurrence.id} excluída com sucesso!`, isOpen: true, statusOk: true })
            setShowDeleteModal(false)
            setDeletingOccurrence(null)
            loadOccurrences(1, false)
        } catch (error: any) {
            console.error('Erro ao excluir ocorrência:', error)
            setFeedback({ message: 'Erro ao excluir ocorrência. Tente novamente.', isOpen: true, statusOk: false })
        } finally {
            setIsDeleting(false)
        }
    }

    const handleCancelDelete = () => {
        setShowDeleteModal(false)
        setDeletingOccurrence(null)
    }

    const handleGeneratePdf = async (occurrence: Occurrence) => {
        try {
            await downloadOccurrencePdfById(occurrence.id)
        } catch (error) {
            console.error('Erro ao gerar PDF da ocorrência:', error)
            setFeedback({ message: 'Não foi possível gerar o PDF da ocorrência. Tente novamente.', isOpen: true, statusOk: false })
        }
    }

const formatProvidencesSummary = (occurrence: Occurrence): string => {
    if (!occurrence.providences || occurrence.providences.length === 0) return '-'
    return occurrence.providences
        .map((p) => {
            const resp = p.responsibleName || 'Sem responsável'
            const deadline = p.deadline ? new Date(p.deadline).toLocaleDateString('pt-BR') : ''
            return `${resp} - ${p.description || ''}${deadline ? ` (${deadline})` : ''}`
        })
        .join(', ')
}

const formatProvidencesTooltip = (occurrence: Occurrence): string => {
    if (!occurrence.providences || occurrence.providences.length === 0) return ''
    return occurrence.providences
        .map((p, idx) => {
            const resp = p.responsibleName || 'Sem responsável'
            const deadline = p.deadline ? new Date(p.deadline).toLocaleDateString('pt-BR') : 'Sem prazo'
            return `${idx + 1}. Responsável: ${resp}\n   Descrição: ${p.description || '-'}\n   Prazo: ${deadline}`
        })
        .join('\n\n')
}

    const columns = useMemo<StandardColumn<Occurrence>[]>(() => [
        {
            id: 'id',
            label: 'ID',
            accessor: (occurrence) => occurrence.id,
        },
        {
            id: 'operationNumber',
            label: 'OP',
            accessor: (occurrence) => occurrence.operationNumber ?? 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={String(occurrence.operationNumber ?? 'N/A')} maxWidth={140} />
            ),
        },
        {
            id: 'companyName',
            label: 'Cliente',
            accessor: (occurrence) => occurrence.companyName || occurrence.contactName || 'N/A',
            cell: (occurrence) => (
                <OverflowCell
                    text={occurrence.companyName || occurrence.contactName || 'N/A'}
                    maxWidth={180}
                />
            ),
        },
        {
            id: 'description',
            label: 'Descrição',
            accessor: (occurrence) => occurrence.description ?? 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={occurrence.description ?? 'N/A'} maxWidth={220} />
            ),
        },
        {
            id: 'area',
            label: 'Área',
            accessor: (occurrence) => occurrence.area || occurrence.Stage?.Sector?.Area?.name || 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={occurrence.area || occurrence.Stage?.Sector?.Area?.name || 'N/A'} maxWidth={160} />
            ),
        },
        {
            id: 'sector',
            label: 'Setor',
            accessor: (occurrence) => occurrence.sector || occurrence.Stage?.Sector?.name || 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={occurrence.sector || occurrence.Stage?.Sector?.name || 'N/A'} maxWidth={160} />
            ),
        },
        {
            id: 'stage',
            label: 'Etapa',
            accessor: (occurrence) => occurrence.Stage?.name ?? 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={occurrence.Stage?.name ?? 'N/A'} maxWidth={160} />
            ),
        },
        {
            id: 'quantity',
            label: 'Quantidade',
            accessor: (occurrence) => (occurrence.quantity !== null && occurrence.quantity !== undefined ? occurrence.quantity : 'N/A'),
        },
        {
            id: 'cause',
            label: 'Causa',
            accessor: (occurrence) => occurrence.cause ?? 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={occurrence.cause ?? 'N/A'} maxWidth={200} />
            ),
        },
        {
            id: 'relevant',
            label: 'Pertinente',
            accessor: (occurrence) => (occurrence.relevant ? 'Sim' : 'Não'),
        },
        {
            id: 'correction',
            label: 'Ação corretiva efetiva',
            accessor: (occurrence) => occurrence.correction ?? 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={occurrence.correction ?? 'N/A'} maxWidth={220} />
            ),
        },
        {
            id: 'providences',
            label: 'Responsável, descrição e prazo',
            accessor: formatProvidencesSummary,
            cell: (occurrence) => {
                const summary = formatProvidencesSummary(occurrence)
                if (summary === '-') return '-'
                return (
                    <OverflowCell text={formatProvidencesTooltip(occurrence)} forceTooltip maxWidth={240}>
                        {summary}
                    </OverflowCell>
                )
            },
        },
        {
            id: 'allocation',
            label: 'Alocação',
            accessor: (occurrence) => occurrence.allocation ?? 'N/A',
            cell: (occurrence) => (
                <OverflowCell text={occurrence.allocation ?? 'N/A'} maxWidth={160} />
            ),
        },
    ], [])

    const { columnsState, handleColumnsChange, handleRestoreColumns } =
        useColumnsState(occurrencesScreenKey, columns)

    return (
        <>
            <TableWrapper>
                <StandardDataTable
                    tableName="Occurrence"
                    columns={columns}
                    data={occurrences}
                    columnsState={columnsState}
                    onColumnsChange={handleColumnsChange}
                    onRestoreColumns={handleRestoreColumns}
                    totalCount={totalOccurrences}
                    onLoadMore={() => loadOccurrences(currentPage + 1, true, search)}
                    hasMore={hasNextPage}
                    isLoading={loading || isLoadingMore}
                    countLabel='ocorrências'
                    exportFileName='ocorrencias'
                    emptyMessage='Nenhuma ocorrência encontrada.'
                    rowActions={(occurrence) => [
                        { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleViewOccurrence(occurrence) },
                        { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEditOccurrence(occurrence) },
                        { label: 'Gerar PDF', icon: <Download size={16} />, onClick: () => handleGeneratePdf(occurrence) },
                        { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDeleteOccurrence(occurrence) },
                    ]}
                />
            </TableWrapper>
            <ConfirmationModal
                isOpen={showDeleteModal}
                title="Excluir Ocorrência"
                message={`Tem certeza que deseja excluir a ocorrência ${deletingOccurrence?.id}? Esta ação não pode ser desfeita.`}
                actions={[
                    {
                        label: 'Cancelar',
                        onClick: handleCancelDelete,
                        variant: 'outline'
                    },
                    {
                        label: isDeleting ? 'Excluindo...' : 'Excluir',
                        onClick: handleConfirmDelete,
                        variant: 'danger'
                    }
                ]}
            />
        </>
    )
}

export default OccurrencesTable
